import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Sparkles } from 'lucide-react';

interface AwardFamily {
  name: string;
  region: 'UK' | 'US' | 'Intl';
  categories: Array<{
    suffix: string;
    genre_focus: 'fiction' | 'nonfiction' | 'translation' | 'lifetime' | 'poetry';
  }>;
}

const AWARD_FAMILIES: AwardFamily[] = [
  {
    name: 'Pulitzer Prize',
    region: 'US',
    categories: [
      { suffix: 'for Fiction', genre_focus: 'fiction' },
      { suffix: 'for History', genre_focus: 'nonfiction' },
      { suffix: 'for Biography', genre_focus: 'nonfiction' },
      { suffix: 'for General Nonfiction', genre_focus: 'nonfiction' },
      { suffix: 'for Memoir', genre_focus: 'nonfiction' },
    ],
  },
  {
    name: 'National Book Award',
    region: 'US',
    categories: [
      { suffix: 'for Fiction', genre_focus: 'fiction' },
      { suffix: 'for Nonfiction', genre_focus: 'nonfiction' },
      { suffix: 'for Poetry', genre_focus: 'poetry' },
      { suffix: 'for Translated Literature', genre_focus: 'translation' },
      { suffix: 'for Young People\'s Literature', genre_focus: 'fiction' },
    ],
  },
];

export function BulkCreateAwards() {
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [creating, setCreating] = useState(false);

  const handleBulkCreate = async () => {
    if (!selectedFamily || !year) {
      toast({
        title: 'Missing information',
        description: 'Please select a prize family and year',
        variant: 'destructive',
      });
      return;
    }

    const family = AWARD_FAMILIES.find(f => f.name === selectedFamily);
    if (!family) return;

    setCreating(true);
    try {
      const awards = family.categories.map(category => ({
        prize_name: `${family.name} ${category.suffix}`,
        year: parseInt(year),
        region: family.region,
        genre_focus: category.genre_focus,
      }));

      const { error } = await supabase
        .from('book_awards')
        .insert(awards);

      if (error) throw error;

      toast({
        title: 'Awards created!',
        description: `Successfully created ${awards.length} ${family.name} category awards for ${year}`,
      });

      // Reset form
      setSelectedFamily('');
      setYear(new Date().getFullYear().toString());

      // Refresh page to show new awards
      window.location.reload();
    } catch (error: any) {
      console.error('Error creating awards:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create awards',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const selectedFamilyData = AWARD_FAMILIES.find(f => f.name === selectedFamily);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Bulk Create Award Categories
        </CardTitle>
        <CardDescription>
          Create all category variants of a prize family at once (e.g., all 5 Pulitzer categories or all 5 National Book Awards)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="family">Prize Family</Label>
            <Select value={selectedFamily} onValueChange={setSelectedFamily}>
              <SelectTrigger id="family">
                <SelectValue placeholder="Select prize family..." />
              </SelectTrigger>
              <SelectContent>
                {AWARD_FAMILIES.map(family => (
                  <SelectItem key={family.name} value={family.name}>
                    {family.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bulk-year">Year</Label>
            <Input
              id="bulk-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="2024"
            />
          </div>
        </div>

        {selectedFamilyData && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Will create {selectedFamilyData.categories.length} awards:</p>
            <ul className="text-sm space-y-1 text-muted-foreground">
              {selectedFamilyData.categories.map((category, index) => (
                <li key={index}>
                  • {selectedFamilyData.name} {category.suffix}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Button
          onClick={handleBulkCreate}
          disabled={creating || !selectedFamily || !year}
          className="w-full"
        >
          {creating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Awards...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Create All {selectedFamilyData?.categories.length || 0} Awards
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}